import { useState, useEffect, ChangeEventHandler } from 'react';
import { useDispatch, useSelector } from '_common/hooks';
import { FormattedMessage, useIntl } from 'react-intl';
import { Popover, Input, InputField, Button, PasswordInput } from '@ets/design-system';
import { navigateToMyFiles } from 'router/navigation';
import { PasswordCheck, SupportButton } from '_common/components';
import { AuthService } from '_common/services';
import { selectAccounts } from 'Auth/redux/localStorageSlice';
import { setAuthenticated, setFinishedFirstLogin } from 'Auth/redux/authSlice';
import AuthHero from 'Auth/components/AuthHero/AuthHero';
import styles from './WelcomePage.module.scss';
import { SessionStorage } from '_common/utils';

const WelcomePage = () => {
  const intl = useIntl();
  const dispatch = useDispatch();

  const userId = useSelector((state) => state.auth.userId);
  const accounts = useSelector(selectAccounts);

  const [loadingBtn, setLoadingBtn] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [department, setDepartment] = useState('');
  const [company, setCompany] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [feedback, setFeedback] = useState({ error: '', value: '' });
  const [disabled, setDisabled] = useState(true);

  const rules = (accounts[userId] && accounts[userId].password_validators) || [];
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (!rules || rules.length === 0) {
      setDisabled(false);
    }
  }, [rules]);

  // Translations
  const VALUES_FOR_FEEDBACK: { [key: string]: string } = {
    username: intl.formatMessage({ id: 'settings.user.username' }),
    first_name: intl.formatMessage({ id: 'settings.name.firstName' }),
    last_name: intl.formatMessage({ id: 'settings.name.lastName' }),
    email_address: intl.formatMessage({ id: 'editor.templatePreview.properties.authors.email' }),
  };

  const onClickNext = () => {
    setLoadingBtn('loadingSpinner');
    const token = SessionStorage.getToken();
    const params = {
      first_name: firstName,
      last_name: lastName,
      password,
      token: token ?? '',
      company,
      department,
    };

    new AuthService({ errorsExpected: [400] })
      .firstLogin(params)
      .then(() => {
        dispatch(setFinishedFirstLogin());
        dispatch(setAuthenticated(true));
        navigateToMyFiles();
      })
      .catch((error) => {
        if (AuthService.isAxiosError(error)) {
          if (error?.response?.status === 400) {
            const errors = error?.response?.data.errors[0];
            const key = errors.errors[0];
            setFeedback({ error: key, value: errors[key] });
          }
        }
        setLoadingBtn('');
      });
  };

  const getFeedbackMessage = () => {
    if (feedback.error) {
      let msg = '';
      switch (feedback.error) {
        case 'password_too_similar':
          msg = intl.formatMessage(
            { id: feedback.error.toUpperCase() },
            { name: VALUES_FOR_FEEDBACK[feedback.value] },
          );
          break;
        case 'password_in_history':
          msg = intl.formatMessage({ id: 'PASSWORD_RECENTLY_USED' });
          break;
        case 'password_too_common':
          msg = intl.formatMessage({ id: 'PASSWORD_TOO_COMMON' });
          break;
        default:
          msg = intl.formatMessage({ id: 'ERROR_SCREEN_HEADER' });
          break;
      }
      return msg;
    }
    return '';
  };

  const handleSetPassword: ChangeEventHandler<HTMLInputElement> = (e) => {
    setPassword(e.target.value);
    setFeedback({ error: '', value: '' });
  };

  const getFormContent = () => {
    return (
      <>
        <div className={styles.name}>
          <div className={styles.inputs} style={{ marginRight: '2rem' }}>
            <InputField
              label={intl.formatMessage({ id: 'settings.name.firstName' })}
              size="l"
              testId="firstName"
              extra={intl.formatMessage({ id: 'REQUIRED' })}
            >
              <span data-testid="firstName">
                <Input
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  autoComplete={'new-password'}
                  placeholder={intl.formatMessage({ id: 'YOUR_FIRST_NAME' })}
                  size="l"
                  testId="first-name"
                />
              </span>
            </InputField>
          </div>
          <div className={styles.inputs}>
            <InputField
              label={intl.formatMessage({ id: 'settings.name.lastName' })}
              size="l"
              testId="lastName"
              extra={intl.formatMessage({ id: 'REQUIRED' })}
            >
              <span data-testid="lastName">
                <Input
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  autoComplete={'new-password'}
                  placeholder={intl.formatMessage({ id: 'YOUR_LAST_NAME' })}
                  size="l"
                  testId="last-name"
                />
              </span>
            </InputField>
          </div>
        </div>
        <div>
          <InputField
            label={intl.formatMessage({ id: 'settings.password.newPassword' })}
            size="l"
            feedbackContent={getFeedbackMessage()}
            style={{ textAlign: 'left' }}
            extra={intl.formatMessage({ id: 'REQUIRED' })}
            testId="newPassword"
          >
            <Popover.Root
              placement="left-start"
              open={isOpen}
              options={{
                offset: {
                  crossAxis: 5,
                  mainAxis: 30,
                },
              }}
              modal={false}
              onOpenChange={() => {
                if (rules.length !== 0) setIsOpen(!isOpen);
              }}
            >
              <Popover.Trigger asChild>
                <div className={styles.password} data-testid="newPassword">
                  <PasswordInput
                    hasError={!!feedback.error}
                    value={password}
                    onChange={handleSetPassword}
                    onFocus={() => {
                      if (rules.length !== 0) {
                        setIsOpen(true);
                      }
                    }}
                    onBlur={(e) => {
                      if (isOpen) {
                        e.target.focus();
                      }
                    }}
                    autoComplete="new-password"
                    placeholder={intl.formatMessage({ id: 'WRITE_YOUR_NEW_PASSWORD' })}
                    size="l"
                    testId="newPassword"
                  />
                </div>
              </Popover.Trigger>
              <Popover.Content testId="password-checker" style={{ minWidth: 'auto' }}>
                <span data-testid="plata">
                  <PasswordCheck rules={rules} password={password} setDisabled={setDisabled} />
                </span>
              </Popover.Content>
            </Popover.Root>
          </InputField>
        </div>
        <div>
          <InputField
            label={intl.formatMessage({ id: 'settings.password.confirmNewPassword' })}
            size="l"
            testId="confirmPassword"
            extra={intl.formatMessage({ id: 'REQUIRED' })}
          >
            <div className={styles.password} data-testid="confirmPassword">
              <PasswordInput
                hasError={!!feedback.error}
                placeholder={intl.formatMessage({ id: 'CONFIRM_YOUR_NEW_PASSWORD' })}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                autoComplete={'new-password'}
                size="l"
                testId="confirmPassword"
              />
            </div>
          </InputField>
        </div>
        <div className={styles.name}>
          <div style={{ marginRight: '2rem' }}>
            <InputField
              label={intl.formatMessage({ id: 'settings.user.company' })}
              size="l"
              testId="company"
            >
              <Input
                value={company}
                onChange={(e) => setCompany(e.target.value)}
                autoComplete={'new-password'}
                placeholder={intl.formatMessage({ id: 'YOUR_COMPANY' })}
                size="l"
                testId="company"
              />
            </InputField>
          </div>
          <div>
            <InputField
              label={intl.formatMessage({ id: 'settings.user.department' })}
              size="l"
              testId="department"
            >
              <Input
                value={department}
                onChange={(e) => setDepartment(e.target.value)}
                autoComplete={'new-password'}
                placeholder={intl.formatMessage({ id: 'YOUR_DEPARTMENT' })}
                size="l"
                testId="department"
              />
            </InputField>
          </div>
        </div>
      </>
    );
  };

  return (
    <div className={styles.root} data-testid="welcome-page">
      <AuthHero>
        <div className={styles.title} data-testid="welcome-page-title">
          <FormattedMessage id="ALMOST_THERE" />
        </div>
        <div className={styles.description} data-testid="welcome-page-description">
          <FormattedMessage id="FIRST_LOGIN_DESCRIPTION" />
        </div>
      </AuthHero>
      <div className={styles.onBoardingRoot}>
        <div className={styles.title}>
          <FormattedMessage id="SET_UP_YOUR_PROFILE" />
        </div>
        <div className={styles.form}>{getFormContent()}</div>
        <div className={styles.footer}>
          <Button
            variant="primary"
            size="l"
            fullWidth
            disabled={
              firstName === '' ||
              lastName === '' ||
              password === '' ||
              password !== confirmPassword ||
              disabled
            }
            loading={!!loadingBtn}
            onClick={onClickNext}
            testId="begin"
          >
            <FormattedMessage id="BEGIN_ONBOARDING" />
          </Button>
        </div>
      </div>
      <SupportButton />
    </div>
  );
};

export default WelcomePage;
